import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Logger, ServerRootService } from '@se/se.ui.html5';
import { PacsStudyData as PacsStudyDataNg1 } from 'estensa-module';
import { isEmpty } from 'lodash-es';
import { Subscription, firstValueFrom, fromEvent } from 'rxjs';

import {
    DEFAULT_OPEN_IMAGES_OPTIONS,
    ExaminationStatusForImaging,
    IViewerHelper,
    KeyImageMessage,
    ModelUSStressEchoAssociateImages,
    PacsStudyDataVnext,
    RequestModelViewStudies,
    USMeasureMessage,
    USStressEchoInitMessage,
    USStressEchoSelectMessage,
    fromNg1PacsStudyData,
    keyImageFromMessage,
} from '../common';
import { isViewerBusy, isViewerOpen, isViewerReady, ViewerStatus } from './models';
import { DeepPascalCase } from '../../../utils/types';
import {
    seViewerConnected,
    seViewerKeyImageAdd,
    seViewerKeyImageRefresh,
    seViewerKeyImagesDelete,
    seViewerKeyImagesSet,
    seViewerUSMeasureDeleteResponse,
    seViewerUSMeasureGetReportResponse,
    seViewerUSMeasureImportRequest,
    seViewerUSStressEchoInit,
    seViewerUSStressEchoSelect,
} from '../../../root-store/root.actions';
import { convertPropertiesToCamelCase, convertPropertiesToPascalCase } from '../../../models/interop';
import { ChildWindow, ChildWindowBuilderService, PopupsBlockedError, ScreenType } from '../../embedding/child.service';
import { BtoaEncoder } from './btoa-encoder';
import { RadioSettingsService } from '../../settings/radio-settings.service';

export type ViewImagesModel = Omit<RequestModelViewStudies, 'studies'> & { studies: Record<string, string>[] };

function viewImagesModelFromPacsStudyData(studyData: PacsStudyDataVnext[]) {
    return {
        ...convertPropertiesToCamelCase<typeof DEFAULT_OPEN_IMAGES_OPTIONS>(DEFAULT_OPEN_IMAGES_OPTIONS),
        studies: studyData.map((datum) => ({
            patientID: datum.patient.patientId,
            patientName: datum.patient.fullName,
            patientSex: datum.patient.sex,
            patientBirthDate: datum.patient.birthDate,
            studyDate: datum.study.studyDate,
            accessionNumber: datum.study.accessionNumber,
            studyInstanceUID: datum.study.studyInstanceUID,
            modality: datum.study.modality,
        })),
    };
}

@Injectable({
    providedIn: 'root',
})
export class SeWebViewerHelper extends ServerRootService implements IViewerHelper {
    readonly isVNext = true;
    readonly viewerType = 'SeWebViewer';

    private readonly baseUrl =
        this.settings.radioSettings.viewerSettings.viewerSeWebSettings?.baseUrl ?? 'https://suse15-dev';
    private readonly keysUrl =
        this.settings.radioSettings.viewerSettings.viewerSeWebSettings?.keysUrl ??
        'https://suse15-dev/auth/api/v1/keys';

    private viewer: ViewerStatus = { state: 'closed' };
    private pingInterval?: any;
    private cryptoKey: CryptoKey;

    constructor(
        private actions$: Actions,
        private childWindowBuilder: ChildWindowBuilderService,
        private http: HttpClient,
        private logger: Logger,
        private settings: RadioSettingsService,
        private store: Store,
        private translate: TranslateService,
        private zone: NgZone,
    ) {
        super();
    }

    /**
     * Required by the interface.
     */
    async init() {
        // empty
    }

    /**
     * Apertura Viewer:
     * - Non fare nulla se il viewer è già aperto e l'utente è già loggato.
     * - Nel caso il viewer sia aperto, ma non l'utente è già loggato, fai solo login.
     * - Altrimenti, apri il viewer, aspetta che sia disponibie per il login, e poi effettua il login.
     */
    async openViewer(studyData?: PacsStudyDataVnext[]) {
        if (isViewerBusy(this.viewer) || isViewerReady(this.viewer)) {
            return;
        }

        if (!isViewerOpen(this.viewer)) {
            await this.openWindow(studyData);
        }

        // Support updating pairing token?
    }

    async closeViewer() {
        if (isViewerOpen(this.viewer)) {
            this.viewer.window.close();
        }
        this.setViewerStateDisconnected();
    }

    private async openWindow(studyData?: PacsStudyDataVnext[]) {
        if ('messagesSubscription' in this.viewer) {
            this.viewer.messagesSubscription.unsubscribe();
        }
        this.viewer = { state: 'opening' };

        try {
            const url = await this.buildViewerUrl(studyData);
            const viewerWindow = await this.childWindowBuilder
                .withUrl(url)
                .onScreen(ScreenType.Extended)
                .withMessageTransformer(new BtoaEncoder())
                .open();

            if (viewerWindow) {
                const messagesSubscription = fromEvent(window, 'message').subscribe((msg: MessageEvent) =>
                    this.zone.run(this.onViewerMessage, this, [msg.data]),
                );
                this.setViewerStateConnected(viewerWindow, messagesSubscription);
                this.listenForActions();
            }
        } catch (err) {
            if (err instanceof PopupsBlockedError) {
                window.alert(this.translate.instant('PopupsBlockedError'));
            }
            this.setViewerStateDisconnected();
        }
    }

    private setViewerStateConnected(window: ChildWindow, messagesSubscription: Subscription) {
        if ('messagesSubscription' in this.viewer) {
            this.viewer.messagesSubscription.unsubscribe();
        }
        this.viewer = { messagesSubscription, state: 'open', window };
        this.store.dispatch(seViewerConnected({ isConnected: true }));
    }

    private setViewerStateDisconnected() {
        if ('messagesSubscription' in this.viewer) {
            this.viewer.messagesSubscription.unsubscribe();
        }
        this.viewer = { state: 'closed' };
        this.store.dispatch(seViewerConnected({ isConnected: false }));
    }

    async discardImages(ask = false) {
        if (!isViewerOpen(this.viewer)) {
            return;
        }

        return this.viewer.window.send({ command: 'DISCARD', payload: { ask } });
    }

    async openImages(studyData: PacsStudyDataVnext[]) {
        if (!isViewerOpen(this.viewer)) {
            await this.openViewer(studyData);
        }
        if (!isViewerOpen(this.viewer)) {
            return;
        }

        return this.viewer.window.send(
            {
                command: 'VIEW_IMAGES',
                payload: await this.encryptPayload(JSON.stringify(viewImagesModelFromPacsStudyData(studyData))),
            },
            { skipTransform: true },
        );
    }

    async setExaminationStatus(idExaminations: string[], examinationStatus: ExaminationStatusForImaging) {
        if (!isViewerOpen(this.viewer)) {
            return;
        }

        return this.viewer.window.send({
            command: 'EXAMINATION_STATUS',
            payload: { idExaminations, examinationStatus },
        });
    }

    async deleteKeyImage?(data: KeyImageMessage): Promise<void> {
        if (!isViewerOpen(this.viewer)) {
            return;
        }

        return this.viewer.window.send({
            command: 'DELETE_KEY_IMAGE',
            payload: data,
        });
    }

    async usStressEchoSelectStage?(stage: string): Promise<void> {
        if (!isViewerOpen(this.viewer)) {
            return;
        }

        return this.viewer.window.send({
            command: 'US_STRESS_ECHO_SELECT_STAGE',
            payload: stage,
        });
    }

    async usStressEchoSelectView?(view: string): Promise<void> {
        if (!isViewerOpen(this.viewer)) {
            return;
        }

        return this.viewer.window.send({
            command: 'US_STRESS_ECHO_SELECT_VIEW',
            payload: view,
        });
    }

    async uSStressEchoAssociateImages?(model: ModelUSStressEchoAssociateImages): Promise<void> {
        if (!isViewerOpen(this.viewer)) {
            return;
        }

        return this.viewer.window.send({
            command: 'US_STRESS_ECHO_SELECT_VIEW',
            payload: model,
        });
    }

    openImagesRequired(imagingData: PacsStudyDataNg1[]) {
        this.openImages(imagingData.map(fromNg1PacsStudyData));
    }

    discardImagesRequired() {
        this.discardImages();
    }

    closeViewerRequired() {
        this.closeViewer();
    }

    isFormSubmitting() {
        return false;
    }

    private onViewerMessage(msg: any) {
        if (!msg || !('command' in msg)) {
            return;
        }
        switch (msg.command) {
            case 'STUDY_INSTANCE_UID':
                return this.onUnimplemented('studyInstanceUID');
            case 'ADD_KEY_IMAGE':
                return this.onAddKeyImage(msg.payload);
            case 'DELETE_KEY_IMAGE':
                return this.onDeleteKeyImage(msg.payload);
            case 'REFRESH_KEY_IMAGE':
                return this.onRefreshKeyImage(msg.payload);
            case 'KEY_IMAGE_INSTANCE_INFO':
                return this.onSetKeyImages(msg.payload);
            case 'US_MEASURE_ASK_FOR_SAVE':
                return this.onUsMeasureImport(msg.payload);
            case 'US_STRESS_ECHO_INIT':
                return this.onUsStressEchoInit(msg.payload);
            case 'US_STRESS_ECHO_SELECT':
                return this.onUsStressEchoSelect(msg.payload);
            case 'ADD_GROUP_MEASURE':
                return this.onUnimplemented('addGroupMeasure');
            case 'DELETE_MEASURE':
                return this.onUnimplemented('deleteMeasure');
            case 'MODIFY_MEASURE':
                return this.onUnimplemented('modifyMeasure');
        }
    }

    private async buildViewerUrl(studyData?: PacsStudyDataVnext[]) {
        const pairingToken = await firstValueFrom(
            this.http.post<any>(`${this.getServerRoot()}/Authentication/GetUserPairingToken`, {}),
        );

        let url = `${this.baseUrl}/internal/${pairingToken.token}`;

        if (!isEmpty(studyData)) {
            const payload = await this.encryptPayload(JSON.stringify(viewImagesModelFromPacsStudyData(studyData)));
            url += `/payload/${payload}`;
        }

        return url;
    }

    private async encryptPayload(payload: string) {
        if (!this.cryptoKey) {
            const jwk = await firstValueFrom(this.http.get(this.keysUrl));
            this.cryptoKey = await window.crypto.subtle.importKey(
                'jwk',
                jwk,
                {
                    name: 'RSA-OAEP',
                    hash: { name: 'SHA-256' },
                },
                false,
                ['encrypt'],
            );
        }

        const encoder = new TextEncoder();
        const encodedPayload = encoder.encode(payload);
        const chunks = [];
        const chunkSize = 128;
        for (let i = 0; i < encodedPayload.length; i += chunkSize) {
            const encryptedBuffer = await crypto.subtle.encrypt(
                { name: 'RSA-OAEP' },
                this.cryptoKey,
                encodedPayload.slice(i, i + chunkSize),
            );
            const b64 = btoa(String.fromCodePoint(...new Uint8Array(encryptedBuffer)));
            chunks.push(b64);
        }

        return chunks.join(':').replace(/\+/g, '-').replace(/\//g, '_');
    }

    //#region Viewer callbacks

    private listenForActions() {
        this.actions$.pipe(ofType(seViewerUSMeasureDeleteResponse)).subscribe((action) => {
            if (isViewerOpen(this.viewer)) {
                this.viewer.window.send({
                    command: action.callback,
                    channel: action.requestId,
                    payload: action.result,
                });
            }
        });
        this.actions$.pipe(ofType(seViewerUSMeasureGetReportResponse)).subscribe((action) => {
            if (isViewerOpen(this.viewer)) {
                this.viewer.window.send({
                    command: action.callback,
                    channel: action.requestId,
                    payload: action.report,
                });
            }
        });

        this.pingInterval = setInterval(() => {
            if (!isViewerOpen(this.viewer)) {
                return;
            }
            if (this.viewer.window.window.closed) {
                clearInterval(this.pingInterval);
                this.setViewerStateDisconnected();
            }
        }, 5000);
    }

    private onAddKeyImage(msg: DeepPascalCase<KeyImageMessage>) {
        this.store.dispatch(
            seViewerKeyImageAdd({
                image: keyImageFromMessage(convertPropertiesToPascalCase(msg)),
            }),
        );
    }

    private onDeleteKeyImage(msg: DeepPascalCase<KeyImageMessage>) {
        this.store.dispatch(
            seViewerKeyImagesDelete({
                images: [keyImageFromMessage(convertPropertiesToPascalCase(msg))],
            }),
        );
    }

    private onRefreshKeyImage(msg: DeepPascalCase<KeyImageMessage>) {
        this.store.dispatch(
            seViewerKeyImageRefresh({
                image: keyImageFromMessage(convertPropertiesToPascalCase(msg)),
            }),
        );
    }

    private onSetKeyImages(msg: DeepPascalCase<KeyImageMessage>[]) {
        this.store.dispatch(seViewerKeyImagesSet({ images: msg.map(keyImageFromMessage) }));
    }

    private onUsMeasureImport(msg: any) {
        this.store.dispatch(seViewerUSMeasureImportRequest(convertPropertiesToCamelCase<USMeasureMessage>(msg)));
    }

    private onUsStressEchoInit(msg: any) {
        this.store.dispatch(seViewerUSStressEchoInit(convertPropertiesToCamelCase<USStressEchoInitMessage>(msg)));
    }

    private onUsStressEchoSelect(msg: any) {
        this.store.dispatch(seViewerUSStressEchoSelect(convertPropertiesToCamelCase<USStressEchoSelectMessage>(msg)));
    }

    private onUnimplemented(messageName: string) {
        return () => {
            this.logger.debug(`Unimplemented viewer message: ${messageName}`);
        };
    }

    //#endregion
}
